import React from 'react';
import { Link } from 'react-router-dom';
import Countdown from 'react-countdown';
import { Gavel, MapPin, Heart, Eye, Package, Bed, Bath, Space, Square } from 'lucide-react';
import i18n from '../../../../i18n';
import { toggleWishlist } from '../../../../models/auctions';
import { formatCurrency } from '../../../../state/utils';
import { BiddingModal } from '../../../helpers/biddingModal';
import { getLoggedUser } from '../../../../state/user';
import '../../../../styles/components/auction-card-real-estate.scss';
import { OmrRiyal } from '../../../common/icons';

const is_logged = localStorage.getItem('is_logged_in') ? true : false;
const user = getLoggedUser();

// ---- Countdown Strip Renderer ----
const CountdownStrip = ({ days, hours, minutes, seconds, completed, isExpired, stripColor }) => {
    const label = isExpired
        ? (i18n.t('bid_time_ends') || 'Bid time ends!!!')
        : (i18n.t('ends_after') || 'End After');

    const units = [
        { val: String(days).padStart(2, '0'), lbl: i18n.t('lbl_day') || 'Day', zeroed: days === 0 },
        { val: String(hours).padStart(2, '0'), lbl: i18n.t('lbl_hour') || 'Hour', zeroed: days === 0 && hours === 0 },
        { val: String(minutes).padStart(2, '0'), lbl: i18n.t('lbl_minute') || 'Minute', zeroed: days === 0 && hours === 0 && minutes === 0 },
        { val: String(seconds).padStart(2, '0'), lbl: i18n.t('lbl_second') || 'Second', zeroed: days === 0 && hours === 0 && minutes === 0 && seconds === 0 },
    ];

    return (
        <div className="re-auc-countdown">
            <div className="re-auc-countdown__inner" style={{ backgroundColor: stripColor }}>
                <span className="re-auc-countdown__label">{label}</span>
                <div className="re-auc-countdown__units">
                    {units.map((u, i) => (
                        <div key={i} className={`re-auc-countdown__unit ${u.zeroed ? 're-auc-countdown__unit--zeroed' : ''}`}>
                            <span className="re-auc-countdown__num">{u.val}</span>
                            <span className="re-auc-countdown__text">{u.lbl}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default class AuctionCardNew extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            wishlist: props.auction.auction_wishlisted || false,
            open_popup: false,
        };
        this._timer = null;
        this.incrementTime = this.incrementTime.bind(this);
    }

    componentDidMount() {
        if (this.props.auction) {
            this._timer = setInterval(this.incrementTime, 1000);
        }
    }

    componentWillUnmount() {
        if (this._timer) clearInterval(this._timer);
    }

    incrementTime() {
        const { auction } = this.props;
        if (!auction) return;
        const dt = new Date(auction.server_time);
        dt.setSeconds(dt.getSeconds() + 1);
        auction.server_time = dt;
        this.setState({});
    }

    async toggleWish() {
        if (!is_logged) return;
        const next = !this.state.wishlist;
        await toggleWishlist({ auction_id: this.props.auction.id, wishlist: next });
        this.setState({ wishlist: next });
    }

    createDate(dt) {
        return dt ? new Date(dt.replace(/-/g, '/')) : null;
    }

    formatDateParts(dateStr) {
        if (!dateStr) return { date: '', time: '', day: '' };
        const d = new Date(dateStr.replace(/-/g, '/'));
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const date = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
        const h = d.getHours(), m = String(d.getMinutes()).padStart(2, '0');
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return { date, time: `${String(h12).padStart(2, '0')}:${m} ${ampm}`, day: dayNames[d.getDay()] };
    }

    render() {
        const { auction } = this.props;
        const lng = localStorage.getItem('I18N_LANGUAGE');
        const { wishlist } = this.state;

        const isExpired = auction.is_bid_time_ends || auction.status !== 'A';
        const now = new Date();
        const isUpcoming = new Date(auction.start_date) > now;

        let stripColor = '#0c8da2';
        let statusClass = 're-auc-badge--active';
        let statusLabel = i18n.t('active') || 'Active';

        if (isExpired) {
            stripColor = '#d93025';
            statusClass = 're-auc-badge--expired';
            statusLabel = i18n.t('expired') || 'Expired';
        } else if (isUpcoming) {
            stripColor = '#bfc531';
            statusClass = 're-auc-badge--upcoming';
            statusLabel = i18n.t('upcoming') || 'Upcoming';
        }

        const countdownTarget = isUpcoming
            ? new Date(this.createDate(auction.start_date)).getTime()
            : new Date(this.createDate(auction.end_date)).getTime();

        // Image
        const images = auction.images || auction.auctionImages || [];
        let imgSrc = process.env.PUBLIC_URL + 'https://test.mzadcom.om/services/public/uploads/groups/image_test_app_no_01.png';
        if (images.length > 0) imgSrc = images[0].image || images[0].url || imgSrc;

        const startParts = this.formatDateParts(auction.start_date);
        const endParts = this.formatDateParts(auction.end_date);
        const location = auction.location || '';

        // Category
        const categoryLabel = lng === 'ar'
            ? (auction?.categoryDetails?.category_name_ar || auction?.categoryDetails?.category_name || 'Apartment type')
            : (auction?.categoryDetails?.category_name || 'Apartment type');

        // Beds / baths / area
        const beds = auction.beds || auction.bedrooms || 5;
        const baths = auction.baths || auction.bathrooms || 2;
        const area = auction.area || auction.size || '500';

        let orgImage = '';
        if (
            auction &&
            auction.organizationDetails &&
            auction.organizationDetails.file_organization_image_full
        ) {
            orgImage = auction.organizationDetails.file_organization_image_full;
        }


        return (
            <>

                <div>
                    <div className="re-auc-card">
                        <span className={`re-auc-badge ${statusClass}`}>
                            {statusLabel}
                        </span>

                        {/* ===== IMAGE SECTION ===== */}
                        <Link to={`/auction-details/${auction.id}`}>
                            <div className="re-auc-img-wrap">
                                <img
                                    src={imgSrc}
                                    alt={auction.title}
                                    className="re-auc-img"
                                />

                                {/* Status Badge */}

                                {/* Location Tag */}
                                {location && (
                                    <div className="re-auc-location">
                                        <MapPin size={10} />
                                        <span>{location}</span>
                                    </div>
                                )}

                                {/* Wishlist */}
                                <button
                                    onClick={() => this.toggleWish()}
                                    className={`re-auc-wishlist ${wishlist ? 're-auc-wishlist--active' : ''}`}
                                >
                                    <Heart
                                        size={14}
                                        fill={wishlist ? '#e74c3c' : 'none'}
                                        color={wishlist ? '#e74c3c' : '#ccc'}
                                    />
                                </button>



                                {/* Countdown */}
                                {!isExpired &&
                                    <Countdown
                                        date={countdownTarget}
                                        now={() => new Date(auction.server_time)}
                                        onComplete={() => this.setState({})}
                                        renderer={(props) => (
                                            <CountdownStrip
                                                {...props}
                                                isExpired={isExpired}
                                                stripColor={stripColor}
                                            />
                                        )}
                                    />}
                            </div>
                        </Link >

                        {/* ===== BODY ===== */}
                        <div className="re-auc-body">
                            <div className="re-auc-topline">
                                <div className="re-auc-topline__info">
                                    <div className="re-auc-amount__label">MZAD-{auction.id}</div>
                                    <div className="re-auc-category">{categoryLabel}</div>
                                    <h3 className="re-auc-title">
                                        {lng === 'ar'
                                            ? (auction.title_ar || auction?.group?.name_ar)
                                            : (auction.title || auction?.group_name)
                                        }
                                    </h3>
                                </div>
                                <div className="re-auc-topline__side">
                                    {orgImage && (
                                        <img src={orgImage} alt="" className="re-auc-topline__org" />
                                    )}
                                    {auction.bid_count !== 0 && <>
                                        <div className="re-auc-topline__bids">
                                            <Gavel size={13} />
                                            <span>{auction.bid_count >= 1000
                                                ? `${(auction.bid_count / 1000).toFixed(1)}k`
                                                : auction.bid_count}
                                            </span>
                                        </div>
                                    </>
                                    }
                                </div>
                            </div>
                            {/* Features */}
                            {/* {(beds || baths || area) && (
                                <div className="re-auc-features">
                                    {beds && <span className="re-auc-feature"><Bed size={15}/> {beds}</span>}
                                    {baths && <span className="re-auc-feature"><Bath size={15}/>  {baths}</span>}
                                    {area && <span className="re-auc-feature"><Square size={15}/>  {area} m²</span>}
                                </div>
                            )} */}

                            {/* Amounts */}
                            <div className="re-auc-amounts">

                                <div className="re-auc-amount">
                                    <span className="re-auc-amount__label">
                                        {i18n.t('total_auctions')}
                                    </span>
                                    <span className="re-auc-amount__value re-auc-amount__value--start">
                                        1 &nbsp;
                                        <Package size={22} />
                                    </span>
                                </div>


                                <div className="re-auc-amount">
                                    <span className="re-auc-amount__label">
                                        {i18n.t('guarantee_amount')}
                                    </span>
                                    <span className="re-auc-amount__value re-auc-amount__value--current">
                                        {formatCurrency(auction.guarantee_amount)}
                                        <OmrRiyal width={22} height={22} />
                                    </span>
                                </div>

                                {/* <div className="re-auc-amount">
                                    <span className="re-auc-amount__label">
                                        {i18n.t('bid_count')}
                                    </span>
                                    <span className="re-auc-amount__value">
                                        {formatCurrency(auction.bid_count)}
                                        <Gavel size={14} />
                                    </span>
                                </div> */}
                                {!auction?.my_rank ? (
                                    <div className="re-auc-amount">
                                        <span className="re-auc-amount__label">
                                            {i18n.t('start_amount')}
                                        </span>
                                        <span className="re-auc-amount__value re-auc-amount__value--current">
                                            {formatCurrency(auction.start_amount)}
                                            <OmrRiyal width={22} height={22} />
                                        </span>
                                    </div>
                                ) : (<div className="re-auc-amount">
                                    <span className="re-auc-amount__label">
                                        {i18n.t('my_rank')}
                                    </span>
                                    <span className="re-auc-amount__value re-auc-amount__value--current">
                                        # {auction.my_rank}
                                    </span>
                                </div>)}

                                <div className="re-auc-amount">
                                    <span className="re-auc-amount__label">
                                        {i18n.t('vat')}
                                    </span>
                                    <span className="re-auc-amount__value">
                                        5 %
                                    </span>
                                </div>


                            </div>

                            {/* Dates */}
                            <div className="re-auc-dates">
                                <div className="re-auc-dates__col">
                                    <span className="re-auc-dates__label">{i18n.t('current_amount')}</span>
                                    <span className="re-auc-dates__pill">{auction.current_amount}
                                        <OmrRiyal width={22} height={22} />
                                    </span>

                                </div>


                                <div className="re-auc-dates__col">
                                    <span className="re-auc-dates__label">{i18n.t('start_date') || 'Start Date'}</span>
                                    <span className="re-auc-dates__date">{startParts.date}</span>
                                    <span className="re-auc-dates__time">{startParts.day}</span>
                                    <span className="re-auc-dates__time">{startParts.time}</span>
                                </div>

                                <div className="re-auc-dates__col">
                                    <span className="re-auc-dates__label">{i18n.t('end_date') || 'End Date'}</span>
                                    <span className="re-auc-dates__date">{endParts.date}</span>
                                    <span className="re-auc-dates__time">{endParts.day}</span>
                                    <span className="re-auc-dates__time">{endParts.time}</span>
                                </div>
                            </div>
                        </div>

                        {/* ===== FOOTER ===== */}
                        <div className="re-auc-footer">
                            <Link
                                to={'/auction-details/' + auction.id}
                                className="re-auc-btn re-auc-btn--details"
                            >
                                <Eye size={13} />
                                {i18n.t('view_details') || 'View Details'}
                            </Link>

                            {auction.status === 'A' && !isExpired && auction.is_enrolled && (
                                <button
                                    onClick={() => this.setState({ open_popup: true })}
                                    className="re-auc-btn re-auc-btn--bid"
                                >
                                    <Gavel size={13} />
                                    {i18n.t('bid_now') || 'Bid Now'}
                                </button>
                            )}

                            {auction.status === 'A' && !auction.is_enrolled && (
                                <Link
                                    to={!is_logged ? '/login' : '/enroll/' + auction.id}
                                    className="re-auc-btn re-auc-btn--enroll"
                                >
                                    {i18n.t('enroll') || 'Enroll'}
                                </Link>
                            )}


                        </div>
                        {/* <div className='re-auc-btn re-auc-btn--enroll'>
                                {i18n.t('final_approval_lbl')}
                            </div> */}
                    </div>
                </div>


                <BiddingModal
                    open_popup={this.state.open_popup}
                    closeModal={() => this.setState({ open_popup: false })}
                    auction={auction}
                    lng={localStorage.getItem('I18N_LANGUAGE')}
                />
            </>
        );
    }
}